<script setup lang="ts">
import type { EventRepresentativeStoryblok } from '@/types'

interface Props {
  representatives: EventRepresentativeStoryblok[]
  isFeatured?: boolean
}
const props = defineProps<Props>()
defineEmits<{ (e: 'click', value: boolean): void }>()
const previewData = computed(() => {
  const representatives = [...props.representatives]
  const restRepresentatives = representatives.splice(
    3,
    representatives.length - 1,
  )

  return {
    representatives,
    restValue: restRepresentatives.length,
  }
})
const ctaLabel = computed(
  () => props.representatives.length > 3 && `+${previewData.value.restValue}`,
)
const numberOfRepresentativesModifier = computed(() => {
  const numberOfDisplayedRepresentatives = props.representatives.length
  if (numberOfDisplayedRepresentatives > 2) {
    return 'event-representatives-preview--more-representatives'
  }

  if (numberOfDisplayedRepresentatives === 1) {
    return 'event-representatives-preview--one-representative'
  }

  return ''
})
</script>

<template>
  <section
    :class="['event-representatives-preview', numberOfRepresentativesModifier]"
  >
    <h5 class="event-representatives-preview__heading">
      {{ $t('common.events.representatives_heading') }}
    </h5>
    <div class="event-representatives-preview__items">
      <div
        v-for="represent in previewData.representatives"
        :key="represent._uid"
        class="event-representatives-preview__item"
      >
        <BaseNuxtImage
          v-if="represent.content.image"
          class="event-representatives-preview__image"
          :src="represent.content.image.filename"
          :alt="represent.content.image.alt"
        />
      </div>
      <BaseButton
        :title="ctaLabel || ''"
        class="event-representatives-preview__btn"
        is-circle
        :has-shadow="isFeatured"
        @click="$emit('click', true)"
      >
        <template v-if="!ctaLabel" #icon>
          <img
            width="16"
            height="16"
            class="event-representatives-preview__icon"
            src="@/assets/icons/16-chevron-left-white.svg?url"
            alt="chevron"
          />
        </template>
        <template v-else #text>
          {{ ctaLabel }}
        </template>
      </BaseButton>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.event-representatives-preview {
  $self: &;

  position: relative;
  z-index: 10;

  &__heading {
    @include heading-subheading;

    margin-bottom: 16px;
  }

  &__image {
    object-fit: contain;
    width: 80px;
    height: 80px;
    border-radius: 2137px;
  }

  &__items {
    display: flex;
    align-items: center;
  }

  &__btn {
    position: relative;
    left: -22px;
  }

  &__item {
    border: 1px solid var(--white);
    border-radius: 50%;
    position: relative;

    &:nth-of-type(2) {
      left: -11px;
    }

    &:nth-of-type(3) {
      left: -22px;
    }
  }

  &__icon {
    transform: rotate(-90deg);
  }

  &--more-representatives {
    #{$self}__btn {
      left: -90px;

      @media (min-width: 360px) {
        left: -33px;
      }
    }

    #{$self}__item {
      &:nth-of-type(2) {
        left: -30px;

        @media (min-width: 360px) {
          left: -11px;
        }
      }

      &:nth-of-type(3) {
        left: -60px;

        @media (min-width: 360px) {
          left: -22px;
        }
      }
    }
  }

  &--one-representative {
    #{$self}__btn {
      left: -11px;
    }
  }
}
</style>
